/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Button, Card, FormControl, FormHelperText, Tooltip, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import {
  deleteFlowConditionCheck,
  deleteFlowTaskInteractionCheck,
  cacheDeleteFlowConditionCheck,
  cacheDeleteFlowTaskInteractionCheck
} from '../../../../../../actions'
import { checkErrorResponse, Logger, LoggerTypes, truncate } from '../../../../../../utils'
import { Icon, iconNames, AccessControl } from '../../../../../../components'
import './style.scss'
import { colorTypes, userRightTypes } from '../../../../../../constants'

/**
 * Display the Interaction object Card either for a `variable` or a `resource`
 *
 * @param {object} currentProject Current project object
 * @param {object} currentScenario Current scenario object
 * @param {object} currentFlowGroup Current flowGroup object
 * @param {object} currentCondition Current condition instance object
 * @param {object} currentCheck  Ex: { variable_id: 'xxx-UUID-xx', operation: 'PLUS', value: 4, type: 'ON_START' }
 * @param {string} type Can be either 'attributesNames.VARIABLES' or 'attributesNames.RESOURCES'
 * @returns {Dialog}
 */
export const FlowElementConditionCard = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentCondition,
  currentTaskInstance,
  currentCheck
  // -- refresh after deleting
  // refreshAfterSaving
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const pid = currentProject?.id || null
  const sid = currentScenario?.id || null
  const fid = currentFlowGroup?.id || null
  const cid = currentCondition?.id || null
  const tid = currentTaskInstance?.id || null

  // -- errors handlers
  const [errors, setErrors] = useState(null)

  const onDelete = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionCard.onDelete()', {
      pid,
      sid,
      fid,
      tid,
      cid,
      currentCheck
    })

    dispatch(
      tid && !cid
        ? deleteFlowTaskInteractionCheck(pid, sid, fid, tid, currentCheck.id)
        : deleteFlowConditionCheck(pid, sid, fid, cid, currentCheck.id)
    )
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionCard.onDelete().dispatch(...).then()', { res })

        // -- reload the FlowGroups data.
        // if (refreshAfterSaving && typeof refreshAfterSaving === 'function') {
        //   refreshAfterSaving()
        // }
        // enqueueSnackbar(t('common.api_success'), { variant: 'success' })

        dispatch(
          tid && !cid
            ? cacheDeleteFlowTaskInteractionCheck(currentFlowGroup, tid, currentCheck.id)
            : cacheDeleteFlowConditionCheck(currentFlowGroup, cid, currentCheck.id)
        )
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'FlowElementConditionCard.onDelete().dispatch(...).catch()', { res })

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data) {
          setErrors(res.response.data)
        }
      })
  }

  // -- left_operand
  // const left_operand_icon = currentCheck?.left_operand?.resource
  //   ? iconNames.RESOURCE
  //   : currentCheck?.left_operand?.variable
  //   ? iconNames.VARIABLE
  //   : currentCheck?.left_operand?.parameter
  //   ? iconNames.PARAMETER
  //   : iconNames.CUSTOM

  const left_operand_name =
    currentCheck?.left_operand?.resource?.name ||
    currentCheck?.left_operand?.variable?.name ||
    currentCheck?.left_operand?.parameter?.name ||
    'N/A'

  // const left_operand_value =
  //   currentCheck?.left_operand?.resource?.quantity ||
  //   currentCheck?.left_operand?.variable?.initial_value ||
  //   currentCheck?.left_operand?.parameter?.value ||
  //   currentCheck?.left_operand?.value ||
  //   null

  // const left_operand_unit =
  //   currentCheck?.left_operand?.resource?.unit ||
  //   currentCheck?.left_operand?.variable?.unit ||
  //   currentCheck?.left_operand?.parameter?.unit ||
  //   null

  // -- right_operand
  const right_operand_icon = currentCheck?.right_operand?.resource
    ? iconNames.RESOURCE
    : currentCheck?.right_operand?.variable
    ? iconNames.VARIABLE
    : currentCheck?.right_operand?.parameter
    ? iconNames.PARAMETER
    : iconNames.CUSTOM

  const right_operand_name =
    currentCheck?.right_operand?.resource?.name ||
    currentCheck?.right_operand?.variable?.name ||
    currentCheck?.right_operand?.parameter?.name ||
    (currentCheck?.right_operand?.value || currentCheck?.right_operand?.value === 0
      ? t('flowActions.conditions.card.custom')
      : null) ||
    null

  const right_operand_unit =
    currentCheck?.right_operand?.resource?.unit ||
    currentCheck?.right_operand?.variable?.unit ||
    currentCheck?.right_operand?.parameter?.unit ||
    null

  let right_operand_value = null
  if (currentCheck?.right_operand?.resource?.quantity || currentCheck?.right_operand?.resource?.quantity === 0) {
    right_operand_value = currentCheck?.right_operand?.resource?.quantity
  } else if (
    currentCheck?.right_operand?.variable?.initial_value ||
    currentCheck?.right_operand?.variable?.initial_value === 0
  ) {
    right_operand_value = currentCheck?.right_operand?.variable?.initial_value
  } else if (currentCheck?.right_operand?.parameter?.value || currentCheck?.right_operand?.parameter?.value === 0) {
    right_operand_value = currentCheck?.right_operand?.parameter?.value
  } else if (currentCheck?.right_operand?.value || currentCheck?.right_operand?.value === 0) {
    right_operand_value = currentCheck?.right_operand?.value
  }

  const maxLen = 14

  // Logger.trace(LoggerTypes.CONTAINER, 'FlowElementConditionCard.render()', {
  //   currentProject,
  //   currentScenario,
  //   currentFlowGroup,
  //   currentCondition,
  //   currentCheck,
  //   left_operand: {
  //     name: left_operand_name
  //     // value: left_operand_value,
  //     // unit: left_operand_unit
  //   },
  //   right_operand: {
  //     name: right_operand_name,
  //     value: right_operand_value,
  //     unit: right_operand_unit
  //   }
  // })

  return (
    <Card className="FlowElementConditionCard common-card">
      <FormControl error={Boolean(errors && errors?.quantity)}>
        {/* -- Left Operand -- */}
        {left_operand_name && left_operand_name.length > maxLen ? (
          <Tooltip title={left_operand_name}>
            <Typography className="left-operand" color={colorTypes.PRIMARY}>
              {truncate(left_operand_name, maxLen)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography className="left-operand" color={colorTypes.PRIMARY}>
            {left_operand_name}
          </Typography>
        )}

        {/* -- Operator -- */}
        <Typography className="operator" color={colorTypes.PRIMARY}>
          {`${String(currentCheck?.operator || '')}`}
        </Typography>

        {/* -- Right Operand -- */}
        {right_operand_name && right_operand_name.length > maxLen ? (
          <Tooltip title={right_operand_name}>
            <Typography className="right-operand" color={colorTypes.PRIMARY} component={'span'}>
              <p className="name">{truncate(right_operand_name, maxLen)}</p>
              <p className="value">
                <Icon name={right_operand_icon} size={12} className="icon" />
                {t('flowActions.conditions.card.value')} {right_operand_value} {right_operand_unit}
              </p>
            </Typography>
          </Tooltip>
        ) : (
          <Typography className="right-operand" color={colorTypes.PRIMARY} component={'span'}>
            <p className="name">{right_operand_name}</p>
            <p className="value">
              <Icon name={right_operand_icon} size={12} className="icon" />
              {t('flowActions.conditions.card.value')} {right_operand_value} {right_operand_unit}
            </p>
          </Typography>
        )}

        {/* -- Delete -- */}
        <AccessControl userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_DELETE]} renderNoAccess={() => <></>}>
          <Tooltip title={t('common.delete')}>
            <Button onClick={onDelete} className="btDelete" color="primary">
              <DeleteOutlineIcon fontSize="small" />
            </Button>
          </Tooltip>
        </AccessControl>

        <FormHelperText>{(errors && errors?.value) || (errors && errors?.quantity) || ''}</FormHelperText>
      </FormControl>
    </Card>
  )
}

FlowElementConditionCard.displayName = 'FlowElementConditionCard'

FlowElementConditionCard.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentCondition: PropTypes.object,
  currentTaskInstance: PropTypes.object,
  currentCheck: PropTypes.object.isRequired
  // -- refresh after saving
  // refreshAfterSaving: PropTypes.func
}

FlowElementConditionCard.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentCondition: null,
  currentTaskInstance: null,
  currentCheck: null // e.g: {id: 'xx-UUID-xx', operation: 'plus', quantity}
  // -- refresh after saving
  // refreshAfterSaving: () => {}
}
